import { Box, Card, CardContent, Typography, Grid, Autocomplete, TextField, Button, Switch } from "@mui/material";
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useLocation } from "react-router-dom";
import Snack from "components/Snack";
import Skeleton from "./skeleton";
import _ from "lodash";
import { updateImCollectionsAccess } from "redux/actions/addEditWorker";
import {
  getImCollectionsConfig,
  mapWorkerImCollectionsToForm,
  buildImCollectionsWorkerPayload,
  getImCollectionsFormComparable,
} from "services/imCollectionsConfig";

const IMCollections = ({ handleChange }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const workerData =
    location.state && location.state.worker ? location.state.worker : null;
  const user = useSelector((state) => state.user.auth.user);
  const imCollectionsData = useSelector(
    (state) => state.addEditWorker.workerRoles.imCollections || {}
  );
  const loadingRoles = useSelector((state) => state.addEditWorker.loadingRoles);
  const imtRole = useSelector((state) => state.user.imtRole || null);
  const [formValues, setFormValues] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [snackControl, setSnackControl] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [disableSave, setDisableSave] = useState(true);
  const [imCollectionsRoleOptions, setImCollectionsRoleOptions] = useState([]);
  const [imCollectionsRolesConfig, setImCollectionsRolesConfig] = useState([]);
  const [imCollectionsPermOptions, setImCollectionsPermOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [readOnly, setReadOnly] = useState(true);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const rolesConfig = await getImCollectionsConfig();
        setImCollectionsRolesConfig(rolesConfig);
        setImCollectionsRoleOptions(rolesConfig.map((role) => role.label));
      } finally {
        setLoadingOptions(false);
      }
    };

    fetchConfig();
  }, []);

  useEffect(() => {
    if (imtRole?.userRole?.role) {
      setReadOnly(imtRole?.userRole?.role === "Support");
    }
  }, [imtRole]);

  useEffect(() => {
    if (imCollectionsData && Object.keys(imCollectionsData).length > 0) {
      setFormValues(
        mapWorkerImCollectionsToForm(imCollectionsData, imCollectionsRolesConfig)
      );
    }
  }, [imCollectionsData, imCollectionsRolesConfig]);

  useEffect(() => {
    if (formValues?.role) {
      const selectedRole = imCollectionsRolesConfig.find(
        (role) =>
          role.label === formValues.role || role.id === formValues.role
      );
      setImCollectionsPermOptions(selectedRole?.additionalPermissions || []);
    } else {
      setImCollectionsPermOptions([]);
    }
    validForm(formValues);
  }, [formValues, imCollectionsRolesConfig]);

  const validForm = (values) => {
    const errors = {};
    if (values?.isActive) {
      if (!values?.role) {
        errors.role = "Field is required";
      }
      if (!values?.collectorId) {
        errors.collectorId = "Field is required";
      }
      const selectedRole = imCollectionsRolesConfig.find(
        (role) =>
          role.label === values?.role || role.id === values?.role
      );
      const permOptions = selectedRole?.additionalPermissions || [];
      if (permOptions.length > 0) {
        const selectedPermissions = (values?.additionalPermissions || []).filter(
          (item) => item.enabled
        );
        if (!selectedPermissions.length) {
          errors.additionalPermissions = "Field is required";
        }
      }
    }
    setFormErrors(errors);
    const isValid =
      Object.keys(errors).length === 0 &&
      !_.isEqual(
        getImCollectionsFormComparable(values),
        getImCollectionsFormComparable(imCollectionsData)
      );
    setDisableSave(!isValid);
    return isValid;
  };

  const handleCloseSnack = () => {
    setSnackControl((state) => ({ ...state, open: false, message: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const isValidForm = validForm(formValues);
      if (!isValidForm) {
        setSnackControl({
          open: true,
          message: "Invalid data to submit. Please check!",
          severity: "info",
        });
        return;
      }

      const data = buildImCollectionsWorkerPayload({
        formValues,
        rolesConfig: imCollectionsRolesConfig,
        actionBy: user?.workerId,
      });

      const response = await dispatch(
        updateImCollectionsAccess(data, workerData)
      );
      if (response && response.status) {
        setSnackControl({
          open: true,
          message: "imCollections Access updated successfully.",
          severity: "success",
        });
      } else {
        setSnackControl({
          open: true,
          message: "imCollections Access update failed.",
          severity: "error",
        });
      }
    } catch (error) {
      setSnackControl({
        open: true,
        message: "imCollections Access failed to submit.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box>
        <form action="" id="formData">
          <Snack
            openSnack={snackControl?.open}
            severity={snackControl?.severity}
            message={snackControl?.message}
            handleCloseSnack={handleCloseSnack}
          ></Snack>

          <Card
            sx={{
              overflow: "auto",
              width: "100%",
              height: "calc(100vh - 160px)",
            }}
          >
            {loadingRoles || loadingOptions ? (
              <Skeleton />
            ) : (
              <CardContent
                style={{ marginLeft: "0%", border: "none", paddingTop: 40 }}
              >
                <Typography variant="h5" color="#616161" marginBottom={3}>
                  IM Collections
                </Typography>
                <hr id="customHr2" />
                <>
                  <Grid container rowSpacing={3} marginTop={2} paddingLeft={3}>
                    <Grid
                      container
                      columnSpacing={3}
                      marginBottom={2}
                      alignItems="center"
                    >
                      <Grid item xs={2} md={2} lg={0.7}>
                        <Typography sx={{ fontSize: "18px" }}>Enable</Typography>
                      </Grid>
                      <Grid item xs={12} md={3}>
                        <Switch
                          inputProps={{
                            "aria-label": "Enable ImCollections Role switch",
                          }}
                          checked={formValues.isActive}
                          onChange={(e) => {
                            const checked = e.target.checked;
                            setFormValues({ ...formValues, isActive: checked });
                          }}
                          disabled={readOnly}
                        />
                      </Grid>
                    </Grid>
                    <Grid container columnSpacing={5} spacing={3} marginTop={3}>
                      <Grid item xs={12} sm={12} md={6} lg={3}>
                        <Autocomplete
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Select Role"
                              required
                              error={Boolean(formErrors?.role)}
                              helperText={formErrors?.role}
                            />
                          )}
                          id="role"
                          name="role"
                          size="small"
                          options={imCollectionsRoleOptions}
                          value={formValues?.role || ""}
                          onChange={(e, v) => {
                            const selectedRole = imCollectionsRolesConfig.find(
                              (role) => role.label === v
                            );
                            setFormValues((prevState) => ({
                              ...prevState,
                              role: v ? v : "",
                              roleId: selectedRole?.id || "",
                              additionalPermissions: [],
                            }));
                          }}
                          disabled={!formValues?.isActive || readOnly}
                          readOnly={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={3}>
                        <Autocomplete
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Additional Permissions"
                              required={imCollectionsPermOptions.length > 0}
                              error={Boolean(formErrors?.additionalPermissions)}
                              helperText={formErrors?.additionalPermissions}
                            />
                          )}
                          id="additionalPermissions"
                          name="additionalPermissions"
                          size="small"
                          multiple
                          disableCloseOnSelect
                          options={imCollectionsPermOptions}
                          getOptionLabel={(option) => option?.label || ""}
                          value={
                            formValues?.additionalPermissions?.filter(
                              (item) => item.enabled
                            ) || []
                          }
                          onChange={(e, v) => {
                            const selectedPermissions = v
                              ? v.map((item) => ({
                                  ...item,
                                  enabled: true,
                                }))
                              : [];
                            setFormValues((prevState) => ({
                              ...prevState,
                              additionalPermissions: selectedPermissions,
                            }));
                          }}
                          isOptionEqualToValue={(option, value) =>
                            value?.enabled &&
                            value?.value &&
                            option.value === value.value
                          }
                          disabled={
                            !formValues?.isActive ||
                            !formValues?.role ||
                            readOnly
                          }
                          readOnly={readOnly}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12} md={6} lg={3}>
                        <TextField
                          sx={{ maxWidth: 300, width: "100%" }}
                          label="Collector ID"
                          required
                          size="small"
                          error={Boolean(formErrors?.collectorId)}
                          helperText={formErrors?.collectorId}
                          value={formValues?.collectorId || ""}
                          disabled={!formValues?.isActive || readOnly}
                          onChange={(e) => {
                            const value = e.target.value.trim() || "";
                            setFormValues((prevState) => ({
                              ...prevState,
                              collectorId: value,
                            }));
                          }}
                          inputProps={{ readOnly: readOnly }}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    item
                    xs={5}
                    style={{ marginTop: "2%", textAlign: "right", marginLeft: 4 }}
                  >
                    <div>
                      <Button
                        size="medium"
                        variant="outlined"
                        style={{
                          marginRight: 10,
                          display: readOnly ? "none" : "",
                        }}
                        onClick={(e) => handleChange(e, 0)}
                      >
                        Cancel
                      </Button>
                      <Button
                        style={{
                          marginRight: 30,
                          display: readOnly ? "none" : "",
                        }}
                        size="medium"
                        variant="contained"
                        id="svBtn"
                        disabled={disableSave}
                        onClick={handleSubmit}
                      >
                        Save
                      </Button>
                    </div>
                  </Grid>
                </>
              </CardContent>
            )}
          </Card>
        </form>
      </Box>
    </>
  );
};

export default IMCollections;
